<template>
  <ConfirmationModal
    :bodyText="bodyText"
    cancelText="cancel"
    continueText="authorize sync"
    :enableMainAction="enableMainAction"
    :handleCancel="handleCancel"
    :handleContinue="handleContinue"
    :headerText="headerText"
    :loading="loading"
  >
    <InputValidator
      :config="{
        autofocus: true,
        label: 'Directory Name',
        name: inputName,
        placeholder: 'ex: JumpCloud',
        validate: 'max:255|uniqueNameValidation|required',
        value: directoryName,
      }"
      @InputValidatorModel="handleValueChange"
    />
    <p
      v-if="kbUrl"
      ref="message"
    >
      {{ messageText }}
      <a
        ref="kbUrl"
        :href="kbUrl"
        rel="noopener noreferrer"
        target="_blank"
      >
        {{ kbLinkText }}
      </a>
    </p>
  </ConfirmationModal>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import ConfirmationModal from 'client/components/Modal/ModalComponents/ConfirmationModal.vue';
import DirectoryUtil from 'client/util/DirectoryUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import InputValidator from 'components/FormValidation/InputValidator.vue';
import ModalEventBus, { ModalEvents } from 'client/components/Modal/ModalEventBus';
import Util from 'client/util/Util';

const {
  description,
  location,
  text,
} = eventProperties;

const {
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTypeConsts,
  StoreModuleMap,
} = Constants;

const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];

export default {
  name: 'CreateDirectoryModal',

  inject: ['$validator'],

  components: {
    ConfirmationModal,
    InputValidator,
  },

  props: {
    directoryType: {
      validator: type => DirectoryNameConsts[type] !== undefined,
      type: String,
      required: true,
    },
    kbLinkText: {
      type: String,
      default: 'Learn More',
    },
    kbUrl: {
      type: String,
      default: '',
    },
    onClick: {
      type: Function,
      required: true,
    },
  },

  data() {
    const displayName = DirectoryNameConsts[this.directoryType];
    return {
      bodyText: `You can import and sync users from multiple ${displayName} directories. Create a custom name to help you identify this directory in the Directories list, then click Authorize Sync to give JumpCloud control to provision, deprovision, and sync user accounts.`,
      directoryName: '',
      enableMainAction: false,
      headerText: `Set Up a New ${displayName} Directory`,
      inputName: 'Directory Name',
      loading: false,
      messageText: `Don't authorize the same ${displayName} directory more than once.`,
    };
  },

  computed: {
    ...mapGetters(directoryCollection, {
      directories: 'getCollection',
    }),

    displayNameConst() {
      return DirectoryNameConsts[this.directoryType];
    },
  },

  beforeMount() {
    this.$validator.extend('uniqueNameValidation', {
      getMessage: `A ${DirectoryNameConsts[this.directoryType]} directory with this name already exists. Enter a unique name.`,
      validate: value => !DirectoryUtil.isDuplicateDirectoryName(
        this.directories,
        {
          name: value.trim(),
          type: this.directoryType,
        },
      ),
    });
  },

  mounted() {
    EventHelper.triggerModalViewed({
      [description]: `Open New ${DirectoryNameConsts[this.directoryType]} Directory Modal`,
      [location]: this.directoryType === DirectoryTypeConsts.activeDirectory
        ? eventLocations.activeDirectoryPage : eventLocations.cloudDirectoriesPage,
      [text]: this.headerText,
    });
  },

  methods: {
    ...mapActions(directoryCollection, ['refreshCollection']),
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    handleCancel() {
      ModalEventBus.close();
    },

    handleContinue() {
      this.loading = true;
      EventHelper.triggerAuthorizeSyncClicked({
        resourceName: this.directoryType,
      });
      ModalEventBus.$once(ModalEvents.transitionEnd, () => {
        this.refreshCollection();
      });

      this.onClick(this.directoryName)
        .then((activateUrl) => {
          const oauthUrl = get(activateUrl, 'data.location');

          Util.redirect(oauthUrl);
        })
        .catch((error) => {
          this.triggerErrorNotification({
            title: `Unable to create ${this.displayNameConst} instance!`,
            message: error && error.message ? error.message : undefined,
          });
        }).finally(() => {
          this.loading = false;
          ModalEventBus.close();
        });
    },

    async handleValueChange(value) {
      await this.$validator.validate(this.inputName);
      this.enableMainAction = (this.v2Errors.items.length === 0);

      if (this.enableMainAction) {
        this.directoryName = value.trim();
      }
    },

    setLocationHash(hash) {
      window.location.hash = hash;
    },
  },
};
</script>
